import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { usePortalAuth } from './context/PortalAuthContext';
import { PortalLayout } from './components/PortalLayout';
import { TokenLanding } from './pages/TokenLanding';
import { Dashboard } from './pages/Dashboard';
import { ServiceHistory } from './pages/ServiceHistory';
import { ServiceDetail } from './pages/ServiceDetail';
import { Invoices } from './pages/Invoices';
import { InvoiceDetail } from './pages/InvoiceDetail';
import { WorkOrderDetail } from './pages/WorkOrderDetail';
import { WorkOrders } from './pages/WorkOrders';
import { Contact } from './pages/Contact';
import { ServiceAgreement } from './pages/ServiceAgreement';
import { QuoteApproval } from './pages/QuoteApproval';
import { AgreementApproval } from './pages/AgreementApproval';
import { PublicBooking } from './pages/PublicBooking';
import { PublicLeadForm } from './pages/PublicLeadForm';
import { SampleReportLayout, SampleServiceReport, SampleInspectionReport } from './pages/SampleReport';
import { Box, CircularProgress, Typography } from '@mui/material';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = usePortalAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Auth route - handles magic link tokens */}
      <Route path="/auth" element={<TokenLanding />} />

      {/* Public quote approval - no auth required */}
      <Route path="/quote/:token" element={<QuoteApproval />} />

      {/* Public agreement approval - no auth required */}
      <Route path="/agreement/:token" element={<AgreementApproval />} />

      {/* Public booking page - no auth required */}
      <Route path="/book" element={<PublicBooking />} />
      <Route path="/book/:slug" element={<PublicBooking />} />
      <Route path="/lead" element={<PublicLeadForm />} />
      <Route path="/lead/:slug" element={<PublicLeadForm />} />

      {/* Public sample reports - no auth required */}
      <Route path="/sample" element={<Navigate to="/sample-service" replace />} />
      <Route path="/sample-service" element={<SampleReportLayout />}>
        <Route index element={<SampleServiceReport />} />
      </Route>
      <Route path="/sample-inspection" element={<SampleReportLayout />}>
        <Route index element={<SampleInspectionReport />} />
      </Route>

      {/* Protected routes with layout */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <PortalLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="service-history" element={<ServiceHistory />} />
        <Route path="service-history/:id" element={<ServiceDetail />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="invoices/:id" element={<InvoiceDetail />} />
        <Route path="work-orders" element={<WorkOrders />} />
        <Route path="work-orders/:id" element={<WorkOrderDetail />} />
        <Route path="service-agreement" element={<ServiceAgreement />} />
        <Route path="contact" element={<Contact />} />
      </Route>

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};
